import { useEffect, useState } from "react";
import { products as api, apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { PageHeader, Card, EmptyState, Loading, ConfirmDelete } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", sku: "", description: "", category: "", price: 0, vat_rate: 15, unit: "each" };

export default function Products() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.list().then(setList).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); };
  const set = (k, num) => (e) => setForm({ ...form, [k]: num ? Number(e.target.value) : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await api.update(editing.id, form); toast.success("Product updated"); }
      else { await api.create(form); toast.success("Product created"); }
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const remove = async (id) => { try { await api.remove(id); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!list) return <Loading />;

  return (
    <div>
      <PageHeader title="Products & Services" subtitle="Your catalogue of billable items.">
        <Button onClick={openNew} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="add-product-button"><Plus className="w-4 h-4 mr-1.5" /> Add Product</Button>
      </PageHeader>

      {list.length === 0 ? (
        <EmptyState icon={Package} title="No products yet" description="Add products or services to quickly add them to invoices and quotes."
          action={<Button onClick={openNew} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="empty-add-product"><Plus className="w-4 h-4 mr-1.5" /> Add Product</Button>} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((p) => (
            <Card key={p.id} className="p-5" data-testid={`product-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  {p.category && <span className="text-[10px] font-semibold uppercase tracking-wider text-[#D4AF37]">{p.category}</span>}
                  <h3 className="font-display font-semibold text-[#0B132B] truncate">{p.name}</h3>
                  {p.sku && <p className="text-xs text-slate-400 font-mono">{p.sku}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                  <ConfirmDelete onConfirm={() => remove(p.id)} title="Delete product?" trigger={<Button variant="ghost" size="icon" className="text-rose-500" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></Button>} />
                </div>
              </div>
              {p.description && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{p.description}</p>}
              <div className="flex items-end justify-between mt-4 pt-4 border-t border-slate-100">
                <div>
                  <div className="font-display text-xl font-bold text-[#0B132B]">{money(p.price)}</div>
                  <div className="text-xs text-slate-400">per {p.unit} · {p.vat_rate}% VAT</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="product-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Product" : "New Product / Service"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Name *</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="product-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>SKU</Label><Input value={form.sku} onChange={set("sku")} className="mt-1.5" data-testid="product-sku-input" /></div>
              <div><Label>Category</Label><Input value={form.category} onChange={set("category")} className="mt-1.5" data-testid="product-category-input" /></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={set("description")} className="mt-1.5" data-testid="product-description-input" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Price (R) *</Label><Input required type="number" step="0.01" value={form.price} onChange={set("price", true)} className="mt-1.5" data-testid="product-price-input" /></div>
              <div><Label>VAT %</Label><Input type="number" step="0.01" value={form.vat_rate} onChange={set("vat_rate", true)} className="mt-1.5" data-testid="product-vat-input" /></div>
              <div><Label>Unit</Label><Input value={form.unit} onChange={set("unit")} className="mt-1.5" data-testid="product-unit-input" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="save-product-button">{saving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
